import React, { useEffect, useState } from "react";
import { ResponsivePie } from "@nivo/pie";

const ComplianceByDayChart = ({ patientData }) => {
  const [chartData, setChartData] = useState([]);
  const [totalCompliantDays, setTotalCompliantDays] = useState(0);
  const [totalPossibleDays, setTotalPossibleDays] = useState(0);

  useEffect(() => {
    if (
      patientData &&
      patientData.month_steps &&
      patientData.month_steps.length >= 2
    ) {
      const dayOfWeekCounts = [0, 0, 0, 0, 0, 0, 0]; // [Mon, Tue, Wed, Thu, Fri, Sat, Sun]
      const stepsData = patientData.month_steps[0] || [];
      const datesData = patientData.month_steps[1] || [];
      let total = 0;

      datesData.forEach((dateStr, index) => {
        if (dateStr && stepsData[index]) {
          const date = new Date(dateStr);
          const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
          const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to 0 = Monday, 6 = Sunday
          // Consider a day compliant if steps are above 5000 (arbitrary threshold)
          if (stepsData[index] > 10) {
            dayOfWeekCounts[adjustedDay]++;
            total++;
          }
        }
      });

      var date1 = new Date(datesData[0]);
      var date2 = new Date((datesData[datesData.length - 1]))
      const totalPossibleDaysData = Math.round((date2 - date1) / (1000 * 60 * 60 * 24))

      if (!isNaN(totalPossibleDaysData)) {
        setTotalPossibleDays(totalPossibleDaysData);
      }

      setTotalCompliantDays(total);

      const dayNames = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ];

      const colors = [
        "#FF6384", // Red
        "#57A0E5", // Blue
        "#FFCE56", // Yellow
        "#3B8543", // Teal
        "#9E59B0", // Purple
        "#FF9F40", // Orange
        "#E6F3FE", // Light Gray
      ];

      // Filter out days with zero count to ensure proper rendering
      const formattedData = dayOfWeekCounts
        .map((count, index) => ({
          id: dayNames[index],
          label: dayNames[index],
          value: count || 0, // Ensure we don't have null/undefined values
          color: colors[index],
        }))
        .filter((item) => item.value > 0); // Only include days with data

      // If we have no data points after filtering, add a dummy value
      if (formattedData.length === 0) {
        formattedData.push({
          id: "No Data",
          label: "No Data",
          value: 1,
          color: "#E7E9ED",
        });
      }

      setChartData(formattedData);
    }
  }, [patientData]);

  // For demo/testing purposes - add dummy data if needed
  useEffect(() => {
    // If no data is provided or processed, create sample data
    if ((!patientData || !patientData.month_steps) && chartData.length === 0) {
      const dummyData = [
        { id: "Monday", label: "Monday", value: 0, color: "#FF6384" },
        { id: "Tuesday", label: "Tuesday", value: 0, color: "#36A2EB" },
        { id: "Wednesday", label: "Wednesday", value: 0, color: "#FFCE56" },
        { id: "Thursday", label: "Thursday", value: 0, color: "#4BC0C0" },
        { id: "Friday", label: "Friday", value: 0, color: "#9966FF" },
        { id: "Saturday", label: "Saturday", value: 0, color: "#FF9F40" },
        { id: "Sunday", label: "Sunday", value: 0, color: "#E7E9ED" },
      ];
      setChartData(dummyData);
      setTotalCompliantDays(0); // Sample total
    }
  }, [patientData, chartData]);

  const theme = {
    background: "transparent",
    textColor: "#333333",
    fontSize: 12,
    axis: {
      domain: { line: { stroke: "#dddddd", strokeWidth: 1 } },
      ticks: {
        line: { stroke: "#dddddd", strokeWidth: 1 },
        text: { fill: "#666666", fontSize: 11 },
      },
      legend: { text: { fill: "#666666", fontSize: 13 } },
    },
    grid: { line: { stroke: "#eeeeee", strokeWidth: 1 } },
    tooltip: {
      container: {
        background: "#ffffff",
        fontSize: 12,
        borderRadius: 3,
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.25)",
        padding: "8px 10px",
      },
    },
  };

  // Custom tooltip component
  const CustomTooltip = ({ datum }) => {
    return (
      <div
        style={{
          padding: "10px 12px",
          background: "#ffffff",
          border: "1px solid #ccc",
          borderRadius: "3px",
          color: "#333",
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
          {datum.label}
        </div>
        <div>
          {datum.value} / {Math.round(totalPossibleDays/7) } day{datum.value !== 1 ? "s" : ""}
        </div>
      </div>
    );
  };

  // If no data, show empty state
  if (!chartData.length) {
    return (
      <div className="graph">
        <h3>Device Use by Day of Week</h3>
        <div style={{ padding: "20px 0", color: "#666", textAlign: "center" }}>
          Not enough data to display compliance by day.
        </div>
      </div>
    );
  }

  return (
    <div className="graph">
      <h3>Device Use by Day of Week</h3>
      <div style={{ height: "360px", width: "100%" }}>
        <ResponsivePie
          data={chartData}
          sortByValue={true}
          theme={theme}
          margin={{ top: 40, right: 120, bottom: 40, left: 40 }}
          innerRadius={0.55}
          padAngle={0.5}
          cornerRadius={4}
          activeOuterRadiusOffset={8}
          colors={{ datum: "data.color" }}
          borderWidth={1}
          borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
          enableArcLabels={false}
          enableArcLinkLabels={false}
          animate={true}
          tooltip={CustomTooltip}
          legends={[
            {
              anchor: "right",
              direction: "column",
              justify: false,
              translateX: 130,
              translateY: 0,
              itemWidth: 100,
              itemHeight: 24,
              itemsSpacing: 10,
              itemTextColor: "#999",
              symbolSize: 16,
              symbolShape: "circle",
              effects: [
                {
                  on: "hover",
                  style: {
                    itemTextColor: "#000",
                  },
                },
              ],
            },
          ]}
          // Add central label with total
          layers={[
            "arcs",
            "arcLabels",
            "arcLinkLabels",
            "legends",
            ({ centerX, centerY }) => {
              return (
                <g>
                  <text
                    x={centerX}
                    y={centerY - 20}
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      fill: "#333",
                    }}
                  >
                    Total
                  </text>
                  <text
                    x={centerX}
                    y={centerY + 15}
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{
                      fontSize: "22px",
                      fontWeight: "bold",
                      fill: "#333",
                    }}
                  >
                    {totalCompliantDays} / {totalPossibleDays}
                  </text>
                </g>
              );
            },
          ]}
        />
      </div>
    </div>
  );
};
export default ComplianceByDayChart;
